import { z } from 'zod';

/**
 * Keyset pagination (§4.7). Offset is forbidden on anything that can exceed 1k rows —
 * on the sites registry (5 214 domains) an offset scan is the difference between 40 ms
 * and 4 s at the tail.
 *
 * A cursor is an opaque base64url of `{ k, id }` where `k` is the sort key value of the
 * last row of the previous page. Sorting is always `(sortKey, id)` so it is total.
 */

export const cursorPayloadSchema = z.object({
  k: z.union([z.string(), z.number(), z.null()]),
  id: z.uuid(),
});
export type CursorPayload = z.infer<typeof cursorPayloadSchema>;

/**
 * base64url without `Buffer`.
 *
 * `contracts` is imported by the SPA as well as by the server, and `Buffer` does not exist
 * in a browser — using it here would have shipped a module that throws the first time a
 * cursor is read client-side.
 */
const B64_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';

function bytesToBase64Url(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i] ?? 0;
    const b1 = bytes[i + 1];
    const b2 = bytes[i + 2];
    out += B64_ALPHABET.charAt(b0 >> 2);
    out += B64_ALPHABET.charAt(((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4));
    if (b1 === undefined) break;
    out += B64_ALPHABET.charAt(((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6));
    if (b2 === undefined) break;
    out += B64_ALPHABET.charAt(b2 & 0x3f);
  }
  return out;
}

function base64UrlToBytes(value: string): Uint8Array {
  const bytes: number[] = [];
  let buffer = 0;
  let bits = 0;
  for (const char of value) {
    const index = B64_ALPHABET.indexOf(char);
    if (index < 0) throw new Error('invalid base64url character');
    buffer = (buffer << 6) | index;
    bits += 6;
    if (bits >= 8) {
      bits -= 8;
      bytes.push((buffer >> bits) & 0xff);
    }
  }
  return Uint8Array.from(bytes);
}

export function encodeCursor(payload: CursorPayload): string {
  return bytesToBase64Url(new TextEncoder().encode(JSON.stringify(payload)));
}

export function decodeCursor(cursor: string): CursorPayload | null {
  try {
    const raw = new TextDecoder().decode(base64UrlToBytes(cursor));
    const parsed = cursorPayloadSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export const MAX_PAGE_SIZE = 200;
export const DEFAULT_PAGE_SIZE = 50;

export const paginationInput = z.object({
  cursor: z.string().nullish(),
  limit: z.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
  direction: z.enum(['asc', 'desc']).default('desc'),
});
export type PaginationInput = z.infer<typeof paginationInput>;

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
  /** Total is only computed when the caller asks — a COUNT over 5k+ rows is not free. */
  total?: number;
}

export function pageSchema<T extends z.ZodType>(item: T) {
  return z.object({
    items: z.array(item),
    nextCursor: z.string().nullable(),
    total: z.number().int().nonnegative().optional(),
  });
}

/**
 * Builds a page from `limit + 1` rows: the extra row proves there is a next page without
 * a second query.
 */
export function buildPage<T extends { id: string }>(
  rows: T[],
  limit: number,
  sortKeyOf: (row: T) => string | number | null,
): Page<T> {
  const hasMore = rows.length > limit;
  const items = hasMore ? rows.slice(0, limit) : rows;
  const last = items.at(-1);
  return {
    items,
    nextCursor: hasMore && last ? encodeCursor({ k: sortKeyOf(last), id: last.id }) : null,
  };
}
